import { useState, useEffect } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import intl from "components/i18n/ReactIntlWrapper";
import { useToastMessageQueue } from '../ToastMessages/ToastMessageProvider';
import CorporateEntitiesNavbar from './CorporateEntitiesNavbar';
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CardHeader
} from "@mui/material";
import { SCOPES } from "common/permissions"
import { DataGrid, GridColDef, GridInitialState, GridSortModel } from '@mui/x-data-grid';
import { DEFAULT_PAGINATION } from 'hooks/useSearch';
import { getUserEntity } from 'common/UserEntityProvider';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { useNavigate } from "react-router";
import { usePaginationPreference } from 'hooks/usePaginationPreference';


export type CorporateEntitiesInput = {
    queryTerm?: string | null,
    idCountry?: number | null,
    currentPage?: number,
    sortValue?: string | null,
    orderValue?: string| null
};

export type SearchResult = {
	totalRecords: number | null,
	queryResult: any
};

const CorporateEntities = () => {

    const [input, setInput] = useState<CorporateEntitiesInput | null>({
        queryTerm: "",
        idCountry: null,
        currentPage: 1,
		sortValue: "",
		orderValue: "asc",
	});

    const [corporateEntities, setCorporateEntities] = useState<SearchResult | null>({
        totalRecords: null,
        queryResult: null
	});

    const [countries, setCountries] = useState(null);

    const [loading, setLoading] = useState<boolean>(false);

    const [errors, setErrors] = useState({});
    
    const api: Api = useApi();

    const toast = useToastMessageQueue();

    const [pageCount, setpageCount] = useState<number>(0);

    const navigate = useNavigate();

	const entityType = getUserEntity();
	
	const [searchTerm, setSearchTerm] = useState<string>("");
    const [pagination, setPagination] = usePaginationPreference('corporateEntities', 10);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "name", sort: "asc" }]);

    const INITIAL_STATE: GridInitialState = {
		pagination: {
		  paginationModel: {
			page: 0,
			pageSize: 10,
		  },
		},
		sorting: {
		  sortModel: [
			{
			  field: 'name',
			  sort: 'asc',
			},
		  ],
		},
	};

    const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 250,
      flex: 1,
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 250,
      flex: 1,
      renderCell: (params) => <>{intl.get(params.row.country.labelKey)}</>,
    },
    {
      field: 'detail',
      headerName: 'Detail',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onEdit={() => navigate(`/corporateEntities/detail/${params.row.id}`)}
            editScopes={[SCOPES['employees.edit']]}
          />
        </Box>
      ),
      }
  ];
  const handleSortModelChange = (model: GridSortModel) => {
      setSortModel(model);
    };

    const fetchCorporateEntities = async (term?: string, pageNum?: number, pageSize?: number, sort?: string, order?: string, f?: Array<string>) => {
        setLoading(true);
        if (input.idCountry) {
            f = [`country:${input.idCountry}`];
        }
        const response = await api.invoiceApi.apiVversionCorporateEntitySearchGet ("1", searchTerm, pagination?.page + 1,
        pagination?.pageSize,
        sortModel[0]?.field || 'name',
        sortModel[0]?.sort || 'asc', f, {}).then((response) => {
            if (response.data.data.queryResult) {
                setCorporateEntities({
                    totalRecords: response.data?.data?.totalRecords,
                    queryResult: response.data?.data?.queryResult
                });
            } else {
                setCorporateEntities({
                    totalRecords: null,
                    queryResult: null
                });
            };
            console.log("Success");
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
        }).finally(() => {
            setLoading(false);
        })
    };

    const fetchCountries = async () => {
        const response = await api.addressApi.apiVversionAddressCountryAllGet("1", {}).then((response) => {
            if (response.data) {
                setCountries(response.data.data);                
            };
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({...errors});
        });
    };

    useEffect(() => {
        fetchCorporateEntities();
	}, [input, searchTerm, pagination, sortModel]);

    useEffect(() => {
        fetchCountries();
    }, []);

    const onSearch = (queryString, idCountry) => {
        setErrors([]);
        setSearchTerm(queryString);
        setPagination({ ...pagination, page: 0 }); // Reset to first page on search
		setInput({ ...input, queryTerm: queryString, idCountry: idCountry, currentPage: 1 });
	};

    return (
        <Card sx={{ m: 2 }}>
        <Box sx={{ p: 3 }}>
              <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                Corporate Entitites
              </Typography>
                <CorporateEntitiesNavbar onSearch={onSearch} countries={countries} />

                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <Spinner />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <ErrorSummary
                        errors={errors}
                        showEverything={false}
                        mode="warning"
                      />
                    </Box>
                
                    <Box sx={{ width: '100%', display: 'flex' }}>
                      {loading && corporateEntities && <Spinner />}
                
                      {corporateEntities && (
                        <DataGrid
                          rows={corporateEntities?.queryResult}
                          columns={columns}
                          rowCount={corporateEntities?.totalRecords || 0}
                          loading={loading}
                          initialState={INITIAL_STATE}
                          paginationModel={pagination}
                          onPaginationModelChange={setPagination}
                          sortModel={sortModel}
                          onSortModelChange={handleSortModelChange}
                          pageSizeOptions={[5, 10, 20]}
                          paginationMode="server"
                          sortingMode="server"
                          autoHeight
                        />
                      )}
                    </Box>
                  </>
                )}
        </Box>
        </Card>
    )
};

export default CorporateEntities;